/**
 * Restaurant Industry Benchmarks
 * Based on industry data for restaurant websites
 */

export interface Percentile {
  p25: number; // Bottom quartile
  p50: number; // Median
  p75: number; // Top quartile
  p90: number; // Top 10%
}

export interface MetricBenchmark {
  name: string;
  unit: string;
  percentiles: Percentile;
  goodThreshold: number;
  betterDirection: 'higher' | 'lower';
  description: string;
}

export interface IndustryBenchmarks {
  performance: MetricBenchmark;
  seo: MetricBenchmark;
  accessibility: MetricBenchmark;
  bestPractices: MetricBenchmark;
  lcp: MetricBenchmark;
  fcp: MetricBenchmark;
  cls: MetricBenchmark;
  ttfb: MetricBenchmark;
  gtmAdoption: number;
  ga4Adoption: number;
  facebookPixelAdoption: number;
}

// Restaurant industry benchmarks based on industry analysis
// Sources: Google Web Vitals reports, restaurant industry studies
export const RESTAURANT_BENCHMARKS: IndustryBenchmarks = {
  performance: {
    name: 'Performance Score',
    unit: '',
    percentiles: { p25: 35, p50: 52, p75: 72, p90: 85 },
    goodThreshold: 90,
    betterDirection: 'higher',
    description: 'Lighthouse performance score measuring load speed, interactivity, and visual stability',
  },
  seo: {
    name: 'SEO Quality Score',
    unit: '',
    percentiles: { p25: 68, p50: 82, p75: 91, p90: 97 },
    goodThreshold: 90,
    betterDirection: 'higher',
    description: 'Custom SEO quality score based on on-page signals and technical checks',
  },
  accessibility: {
    name: 'Accessibility Score',
    unit: '',
    percentiles: { p25: 72, p50: 84, p75: 92, p90: 98 },
    goodThreshold: 90,
    betterDirection: 'higher',
    description: 'Lighthouse accessibility score measuring WCAG compliance',
  },
  bestPractices: {
    name: 'Best Practices Score',
    unit: '',
    percentiles: { p25: 67, p50: 79, p75: 92, p90: 100 },
    goodThreshold: 90,
    betterDirection: 'higher',
    description: 'Lighthouse best practices score measuring web development standards',
  },
  lcp: {
    name: 'Largest Contentful Paint',
    unit: 's',
    percentiles: { p25: 5.2, p50: 3.8, p75: 2.4, p90: 1.8 },
    goodThreshold: 2.5,
    betterDirection: 'lower',
    description: 'Time until the largest content element is painted (target: <2.5s)',
  },
  fcp: {
    name: 'First Contentful Paint',
    unit: 's',
    percentiles: { p25: 3.8, p50: 2.5, p75: 1.6, p90: 1.2 },
    goodThreshold: 1.8,
    betterDirection: 'lower',
    description: 'Time until the first content is painted (target: <1.8s)',
  },
  cls: {
    name: 'Cumulative Layout Shift',
    unit: '',
    percentiles: { p25: 0.35, p50: 0.18, p75: 0.08, p90: 0.03 },
    goodThreshold: 0.1,
    betterDirection: 'lower',
    description: 'Visual stability score measuring layout shifts (target: <0.1)',
  },
  ttfb: {
    name: 'Time to First Byte',
    unit: 's',
    percentiles: { p25: 1.8, p50: 1.2, p75: 0.7, p90: 0.4 },
    goodThreshold: 0.8,
    betterDirection: 'lower',
    description: 'Server response time (target: <0.8s)',
  },
  gtmAdoption: 0.72, // 72% of restaurant websites use GTM
  ga4Adoption: 0.68, // 68% have GA4
  facebookPixelAdoption: 0.45, // 45% have Facebook Pixel
};

export type BenchmarkMetric = keyof Omit<IndustryBenchmarks, 'gtmAdoption' | 'ga4Adoption' | 'facebookPixelAdoption'>;

/**
 * Get the percentile ranking for a given value
 */
export function getPercentileRank(
  value: number,
  benchmark: MetricBenchmark
): number {
  const { percentiles, betterDirection } = benchmark;

  // For metrics where lower is better, invert the comparison
  const compare = betterDirection === 'higher'
    ? (v: number, p: number) => v >= p
    : (v: number, p: number) => v <= p;

  if (compare(value, percentiles.p90)) return 90;
  if (compare(value, percentiles.p75)) return 75;
  if (compare(value, percentiles.p50)) return 50;
  if (compare(value, percentiles.p25)) return 25;
  return 10; // Below p25
}

/**
 * Get a human-readable percentile description
 */
export function getPercentileDescription(percentile: number): string {
  if (percentile >= 90) return 'Top 10%';
  if (percentile >= 75) return 'Top 25%';
  if (percentile >= 50) return 'Above Average';
  if (percentile >= 25) return 'Below Average';
  return 'Bottom 25%';
}

/**
 * Get color class for percentile
 * Aligned with app-wide color palette with reduced opacity for subtlety
 */
export function getPercentileColor(percentile: number): string {
  if (percentile >= 90) return 'text-green-500/80';
  if (percentile >= 75) return 'text-green-500/70';
  if (percentile >= 50) return 'text-orange-400/80';
  if (percentile >= 25) return 'text-orange-500/80';
  return 'text-red-500/80';
}

/**
 * Compare a site's metrics against industry benchmarks
 */
export function compareToBenchmarks(metrics: {
  performance: number;
  seo?: number;
  accessibility: number;
  bestPractices: number;
  lcp: number;
  fcp: number;
  cls: number;
  ttfb: number;
}): Record<BenchmarkMetric, { percentile: number; description: string; isAboveTarget: boolean }> {
  const benchmarks = RESTAURANT_BENCHMARKS;

  return {
    performance: {
      percentile: getPercentileRank(metrics.performance, benchmarks.performance),
      description: getPercentileDescription(getPercentileRank(metrics.performance, benchmarks.performance)),
      isAboveTarget: metrics.performance >= benchmarks.performance.goodThreshold,
    },
    seo: {
      percentile: getPercentileRank(metrics.seo ?? 0, benchmarks.seo),
      description: getPercentileDescription(getPercentileRank(metrics.seo ?? 0, benchmarks.seo)),
      isAboveTarget: (metrics.seo ?? 0) >= benchmarks.seo.goodThreshold,
    },
    accessibility: {
      percentile: getPercentileRank(metrics.accessibility, benchmarks.accessibility),
      description: getPercentileDescription(getPercentileRank(metrics.accessibility, benchmarks.accessibility)),
      isAboveTarget: metrics.accessibility >= benchmarks.accessibility.goodThreshold,
    },
    bestPractices: {
      percentile: getPercentileRank(metrics.bestPractices, benchmarks.bestPractices),
      description: getPercentileDescription(getPercentileRank(metrics.bestPractices, benchmarks.bestPractices)),
      isAboveTarget: metrics.bestPractices >= benchmarks.bestPractices.goodThreshold,
    },
    lcp: {
      percentile: getPercentileRank(metrics.lcp, benchmarks.lcp),
      description: getPercentileDescription(getPercentileRank(metrics.lcp, benchmarks.lcp)),
      isAboveTarget: metrics.lcp <= benchmarks.lcp.goodThreshold,
    },
    fcp: {
      percentile: getPercentileRank(metrics.fcp, benchmarks.fcp),
      description: getPercentileDescription(getPercentileRank(metrics.fcp, benchmarks.fcp)),
      isAboveTarget: metrics.fcp <= benchmarks.fcp.goodThreshold,
    },
    cls: {
      percentile: getPercentileRank(metrics.cls, benchmarks.cls),
      description: getPercentileDescription(getPercentileRank(metrics.cls, benchmarks.cls)),
      isAboveTarget: metrics.cls <= benchmarks.cls.goodThreshold,
    },
    ttfb: {
      percentile: getPercentileRank(metrics.ttfb, benchmarks.ttfb),
      description: getPercentileDescription(getPercentileRank(metrics.ttfb, benchmarks.ttfb)),
      isAboveTarget: metrics.ttfb <= benchmarks.ttfb.goodThreshold,
    },
  };
}

/**
 * Get benchmark comparison summary for a site
 */
export function getBenchmarkSummary(metrics: {
  performance: number;
  seo: number;
  accessibility: number;
  bestPractices: number;
}): {
  averagePercentile: number;
  metricsAboveMedian: number;
  metricsInTop25: number;
  overallRating: 'excellent' | 'good' | 'average' | 'below-average' | 'poor';
} {
  const benchmarks = RESTAURANT_BENCHMARKS;

  const percentiles = [
    getPercentileRank(metrics.performance, benchmarks.performance),
    getPercentileRank(metrics.accessibility, benchmarks.accessibility),
    getPercentileRank(metrics.bestPractices, benchmarks.bestPractices),
  ];

  const averagePercentile = Math.round(percentiles.reduce((a, b) => a + b, 0) / percentiles.length);
  const metricsAboveMedian = percentiles.filter(p => p >= 50).length;
  const metricsInTop25 = percentiles.filter(p => p >= 75).length;

  let overallRating: 'excellent' | 'good' | 'average' | 'below-average' | 'poor';
  if (averagePercentile >= 90) overallRating = 'excellent';
  else if (averagePercentile >= 75) overallRating = 'good';
  else if (averagePercentile >= 50) overallRating = 'average';
  else if (averagePercentile >= 25) overallRating = 'below-average';
  else overallRating = 'poor';

  return {
    averagePercentile,
    metricsAboveMedian,
    metricsInTop25,
    overallRating,
  };
}
